import { z } from "zod";
import { IsoTimestamp, MatchId, NightId, PlayerId } from "./ids.js";

// Match state machine v0.2 — MATCH POINT §5.3 (card flow primary, scheduled flow
// secondary) + FAIR RESULT outcomes (walkover / void-no-fault are first-class,
// never overloaded onto COMPLETE). See D-006 in docs/DECISIONS.md.
//
// CARD FLOW (primary):
//   (card_posted creates the match in LOBBY_ARMED — host created the EA lobby
//    and relayed the link into BLG via share-target/paste/typed/OCR)
//   LOBBY_ARMED ──── card_live ───────────▶ CARD_LIVE       (5:00 feed countdown)
//   CARD_LIVE ────── card_claimed ────────▶ CLAIMED         (atomic, first tap wins)
//   CARD_LIVE ────── card_expired ────────▶ EXPIRED         (0:00 → host re-arm prompt)
//   CARD_LIVE ────── card_withdrawn ──────▶ WITHDRAWN       (pre-claim, max 3/night)
//   CLAIMED ──────── link_opened ─────────▶ IN_GAME         (joiner into the EA lobby)
//   CLAIMED ──────── walkover_awarded ────▶ WALKOVER        (no-show, one-sided ledger)
//   CLAIMED ──────── match_voided ────────▶ VOIDED
//   IN_GAME ──────── result_window_opened ▶ RESULT_PENDING  (timer/foreground ≥8min)
//   IN_GAME ──────── result_submitted ────▶ VERIFICATION    (early submit — D-006 deviation)
//   IN_GAME ──────── walkover_awarded ────▶ WALKOVER
//   IN_GAME ──────── match_voided ────────▶ VOIDED          (ambiguous telemetry)
//   RESULT_PENDING ─ result_submitted ────▶ VERIFICATION
//   RESULT_PENDING ─ walkover_awarded ────▶ WALKOVER        (claim-timer lapse)
//   RESULT_PENDING ─ match_voided ────────▶ VOIDED          (T+12h, both silent)
//   VERIFICATION ─── result_edited ───────▶ VERIFICATION    (free edit window, on the ledger)
//   VERIFICATION ─── result_verified ─────▶ COMPLETE        (opponent verify or auto-confirm)
//   VERIFICATION ─── dispute_raised ──────▶ DISPUTED
//   DISPUTED ─────── dispute_resolved ────▶ COMPLETE        (concede / Tier 2 agent / Tier 3 human)
//   DISPUTED ─────── walkover_awarded ────▶ WALKOVER
//   DISPUTED ─────── match_voided ────────▶ VOIDED          (void-no-fault)
//
// SCHEDULED FLOW (brackets, pre-arranged kickoffs):
//   (match_paired creates the match in PAIRED)
//   PAIRED ───────── ready_check_opened ──▶ READY_CHECK
//   PAIRED ───────── match_voided ────────▶ VOIDED          (bracket repair: replace/void)
//   READY_CHECK ──── match_lobby_armed ───▶ LOBBY_ARMED     (joins card flow; card skipped —
//   READY_CHECK ──── walkover_awarded ────▶ WALKOVER         LOBBY_ARMED → CLAIMED direct)
//   READY_CHECK ──── match_voided ────────▶ VOIDED
//   LOBBY_ARMED ──── card_claimed ────────▶ CLAIMED         (link straight to the paired opponent)
export const MatchState = z.enum([
  "PAIRED",
  "READY_CHECK",
  "LOBBY_ARMED",
  "CARD_LIVE",
  "CLAIMED",
  "IN_GAME",
  "RESULT_PENDING",
  "VERIFICATION",
  "DISPUTED",
  "COMPLETE",
  "WALKOVER",
  "VOIDED",
  "EXPIRED",
  "WITHDRAWN",
]);
export type MatchState = z.infer<typeof MatchState>;

export const MATCH_TERMINAL_STATES: readonly MatchState[] = [
  "COMPLETE",
  "WALKOVER",
  "VOIDED",
  "EXPIRED",
  "WITHDRAWN",
];

export const MatchFlow = z.enum(["card", "scheduled"]);
export type MatchFlow = z.infer<typeof MatchFlow>;

export const LinkIngestMethod = z.enum(["share_target", "paste", "typed", "ocr"]);
export type LinkIngestMethod = z.infer<typeof LinkIngestMethod>;

export const MatchScore = z.object({
  host_goals: z.number().int().min(0).max(99),
  joiner_goals: z.number().int().min(0).max(99),
});
export type MatchScore = z.infer<typeof MatchScore>;

// Supabase `matches` row (KICKOFF §4.1) — timestamps per transition.
export const Match = z.object({
  id: MatchId,
  host_id: PlayerId,
  joiner_id: PlayerId.nullable(),
  state: MatchState,
  flow: MatchFlow,
  match_ext_id: z.string().regex(/^\d{6}$/).nullable(), // EA Quick Match ID
  link_hash: z.string().min(1), // hash of the relayed EA share link (raw token treated as ephemeral)
  ea_join_url: z.string().url().nullable(), // s.fcm.ea.com?p=… — never cached across matches
  night_id: NightId,
  final_score: MatchScore.nullable(),
  outcome_reason: z.string().nullable(), // reason_code on WALKOVER/VOIDED/dispute verdicts
  created_at: IsoTimestamp,
  card_live_at: IsoTimestamp.nullable(),
  claimed_at: IsoTimestamp.nullable(),
  link_opened_at: IsoTimestamp.nullable(),
  first_submit_at: IsoTimestamp.nullable(),
  resolved_at: IsoTimestamp.nullable(), // terminal-state entry
});
export type Match = z.infer<typeof Match>;

// VAR decision stamps (UPSTAIRS V7): the five outcomes as the players see them.
export const VarVerdict = z.enum([
  "RESULT_STANDS",
  "RESULT_OVERTURNED",
  "WALKOVER",
  "VOID_NO_FAULT",
  "SANCTION",
]);
export type VarVerdict = z.infer<typeof VarVerdict>;

export const DisputeTier = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type DisputeTier = z.infer<typeof DisputeTier>;

export const DisputeResolver = z.enum(["rules", "agent", "human"]);
export type DisputeResolver = z.infer<typeof DisputeResolver>;
